/**
 * Side inspector for selected protocol DAG nodes, edges, and pending connects.
 */

import type {
  ProtocolAtom,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolGate,
} from "../../api/clinicalTrialsApiTypes";
import type { ProtocolConnectionType } from "./protocolDagConnectionRules";
import type { ProtocolDagEdgeData, ProtocolDagNodeData } from "./protocolDagModel";

export type PendingConnection = {
  fromNodeId: string;
  fromNodeType: string;
  toNodeId: string;
  toNodeType: string;
  options: ProtocolConnectionType[];
};

type ProtocolDagInspectorProps = {
  selectedNode: ProtocolDagNodeData | null;
  selectedEdge: ProtocolDagEdgeData | null;
  pendingConnection: PendingConnection | null;
  criteria: ProtocolCriterion[];
  atoms: ProtocolAtom[];
  gates: ProtocolGate[];
  connections: ProtocolConnection[];
  readOnly: boolean;
  busy?: boolean;
  onExpandCriterion: (criterionId: string) => void;
  onChooseConnectionType: (connectionType: ProtocolConnectionType) => void;
  onCancelPending: () => void;
  onRemoveConnection: (connectionId: string) => void;
  onClearSelection: () => void;
};

/**
 * Render detail/actions for the current DAG selection or pending connection.
 *
 * @param props - Inspector props
 * @returns Inspector panel element
 */
export function ProtocolDagInspector({
  selectedNode,
  selectedEdge,
  pendingConnection,
  criteria,
  atoms,
  gates,
  connections,
  readOnly,
  busy = false,
  onExpandCriterion,
  onChooseConnectionType,
  onCancelPending,
  onRemoveConnection,
  onClearSelection,
}: ProtocolDagInspectorProps) {
  if (pendingConnection) {
    return (
      <aside className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4 lg:w-80">
        <h3 className="text-sm font-semibold text-slate-900">Add connection</h3>
        <p className="mt-2 font-mono text-xs text-slate-700">
          {pendingConnection.fromNodeId} → {pendingConnection.toNodeId}
        </p>
        <p className="mt-2 text-xs text-slate-600">
          Choose a compatible connection type:
        </p>
        <div className="mt-3 flex flex-col gap-2">
          {pendingConnection.options.map((option) => (
            <button
              key={option}
              disabled={busy || readOnly}
              className="rounded border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:border-slate-900 disabled:opacity-50"
              onClick={() => onChooseConnectionType(option)}
            >
              {option}
            </button>
          ))}
        </div>
        <button
          className="mt-3 text-xs text-slate-600 hover:underline"
          onClick={onCancelPending}
        >
          Cancel
        </button>
      </aside>
    );
  }

  if (selectedEdge) {
    const connection = connections.find(
      (item) => item.connection_id === selectedEdge.connectionId,
    );
    return (
      <aside className="w-full rounded-lg border border-slate-200 bg-white p-4 lg:w-80">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">Connection</h3>
          <button
            className="text-xs text-slate-500 hover:underline"
            onClick={onClearSelection}
          >
            Close
          </button>
        </div>
        <dl className="mt-3 space-y-2 text-xs text-slate-700">
          <div>
            <dt className="font-semibold text-slate-500">Type</dt>
            <dd>{selectedEdge.connectionType}</dd>
          </div>
          <div>
            <dt className="font-semibold text-slate-500">From</dt>
            <dd className="font-mono">{connection?.from_node_id}</dd>
          </div>
          <div>
            <dt className="font-semibold text-slate-500">To</dt>
            <dd className="font-mono">{connection?.to_node_id}</dd>
          </div>
          {selectedEdge.proxy ? (
            <p className="rounded bg-slate-50 px-2 py-1 text-slate-600">
              Dashed edge is proxied through a collapsed criterion. Expand the
              criterion to edit the exact endpoints.
            </p>
          ) : null}
        </dl>
        {!readOnly && !selectedEdge.proxy ? (
          <button
            disabled={busy}
            className="mt-4 rounded border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50"
            onClick={() => onRemoveConnection(selectedEdge.connectionId)}
          >
            Remove connection
          </button>
        ) : null}
      </aside>
    );
  }

  if (selectedNode) {
    const criterion = criteria.find(
      (item) => item.criterion_id === selectedNode.criterionId,
    );
    const atom =
      selectedNode.kind === "atom"
        ? atoms.find((item) => item.atom_id === selectedNode.entityId)
        : null;
    const gate =
      selectedNode.kind === "gate"
        ? gates.find((item) => item.gate_id === selectedNode.entityId)
        : null;
    return (
      <aside className="w-full rounded-lg border border-slate-200 bg-white p-4 lg:w-80">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900">
            {selectedNode.kind} detail
          </h3>
          <button
            className="text-xs text-slate-500 hover:underline"
            onClick={onClearSelection}
          >
            Close
          </button>
        </div>
        <div className="mt-3 space-y-2 text-xs text-slate-700">
          <div className="font-mono text-slate-900">{selectedNode.entityId}</div>
          <p>{selectedNode.subtitle}</p>
          {atom ? (
            <ul className="space-y-1 text-slate-600">
              <li>Tier {atom.tier}</li>
              <li>{atom.pass_condition}</li>
              <li>{atom.failure_type}</li>
              {atom.logic_group ? <li>Gate {atom.logic_group}</li> : null}
            </ul>
          ) : null}
          {gate ? (
            <p>
              Operator {gate.operator}
              {gate.is_hard_stop ? " · hard-stop" : ""}
            </p>
          ) : null}
          {criterion && selectedNode.kind === "criterion" ? (
            <p className="text-slate-600">
              Eval order {criterion.eval_order} ·{" "}
              {criterion.criterion_text_verbatim.slice(0, 160)}
              {criterion.criterion_text_verbatim.length > 160 ? "…" : ""}
            </p>
          ) : null}
        </div>
        {selectedNode.kind === "criterion" ? (
          <button
            className="mt-4 rounded border border-slate-300 px-3 py-1.5 text-sm"
            onClick={() => onExpandCriterion(selectedNode.criterionId)}
          >
            {selectedNode.expanded ? "Collapse criterion" : "Expand criterion"}
          </button>
        ) : null}
        <p className="mt-3 text-[11px] text-slate-500">
          Drag from a node handle to another node to add a connection. Edit atom
          text in the Table view.
        </p>
      </aside>
    );
  }

  return (
    <aside className="w-full rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 lg:w-80">
      <h3 className="text-sm font-semibold text-slate-800">Graph editor</h3>
      <ul className="mt-3 list-disc space-y-1 pl-4 text-xs">
        <li>Click a node to inspect it without moving the viewport.</li>
        <li>Double-click a criterion (or use Expand criterion) to show atoms.</li>
        <li>Use Expand for almost full-screen; Esc exits.</li>
        <li>Drag between handles to add a connection; select an edge to remove.</li>
      </ul>
    </aside>
  );
}
